import { footprintOf, inDoorway, PROPS } from '../props.js';
export function propOrigin(placed) {
    return `${placed.prop}@${String(placed.x)},${String(placed.y)}`;
}
export function spawnOrigin(den) {
    return `${den.mob}@${String(den.x)},${String(den.y)}`;
}
export function applyWorldEdits(base, edits) {
    const stale = [];
    const propEdits = new Map(edits.props.map((edit) => [propOrigin(edit), edit]));
    const portalMoves = new Map();
    const portals = base.portals.map((portal) => {
        const edit = edits.portals.find((candidate) => candidate.id === portal.id);
        if (edit === undefined)
            return portal;
        const dx = edit.x - portal.x;
        const dy = edit.y - portal.y;
        portalMoves.set(`${String(portal.x)},${String(portal.y)}`, { x: edit.x, y: edit.y });
        return {
            ...portal,
            x: edit.x,
            y: edit.y,
            arrival: { x: portal.arrival.x + dx, y: portal.arrival.y + dy },
        };
    });
    for (const edit of edits.portals) {
        if (!base.portals.some((portal) => portal.id === edit.id))
            stale.push(`portail ${edit.id}`);
    }
    const props = [];
    const used = new Set();
    for (const placed of base.props) {
        const origin = propOrigin(placed);
        const edit = propEdits.get(origin);
        if (placed.prop === 'portal') {
            const moved = portalMoves.get(`${String(placed.x)},${String(placed.y)}`);
            props.push({ placement: moved === undefined ? placed : { ...placed, ...moved }, origin });
            continue;
        }
        if (edit === undefined || used.has(origin)) {
            props.push({ placement: placed, origin });
            continue;
        }
        used.add(origin);
        if (edit.to === null)
            continue;
        props.push({ placement: { prop: placed.prop, ...edit.to }, origin });
    }
    for (const edit of edits.props) {
        if (!used.has(propOrigin(edit)))
            stale.push(`décor ${propOrigin(edit)}`);
    }
    edits.added.forEach((placed, index) => {
        props.push({ placement: placed, origin: `added:${String(index)}` });
    });
    const spawnEdits = new Map(edits.spawns.map((edit) => [spawnOrigin(edit), edit]));
    const spawns = [];
    const movedSpawns = new Set();
    for (const den of base.dens) {
        const origin = spawnOrigin(den);
        const edit = spawnEdits.get(origin);
        if (edit === undefined || movedSpawns.has(origin)) {
            spawns.push({ den, origin });
            continue;
        }
        movedSpawns.add(origin);
        if (edit.to === null)
            continue;
        const { facing, count, radius, ...tile } = edit.to;
        spawns.push({
            den: {
                ...den,
                ...tile,
                ...(facing !== undefined ? { facing } : {}),
                ...(count !== undefined ? { count } : {}),
                ...(radius !== undefined ? { radius } : {}),
            },
            origin,
        });
    }
    edits.addedSpawns.forEach(({ facing, ...den }, index) => {
        spawns.push({
            den: { ...den, ...(facing !== undefined ? { facing } : {}) },
            origin: `added-spawn:${String(index)}`,
        });
    });
    for (const edit of edits.spawns) {
        if (!movedSpawns.has(spawnOrigin(edit)))
            stale.push(`habitant ${spawnOrigin(edit)}`);
    }
    const chests = base.chests.map((chest) => {
        const edit = edits.chests.find((candidate) => candidate.id === chest.id);
        return edit === undefined ? chest : { ...chest, x: edit.x, y: edit.y };
    });
    for (const edit of edits.chests) {
        if (!base.chests.some((chest) => chest.id === edit.id))
            stale.push(`coffre ${edit.id}`);
    }
    return {
        layout: {
            ...base,
            props: props.map((prop) => prop.placement),
            dens: spawns.map((spawn) => spawn.den),
            portals,
            chests,
        },
        props,
        spawns,
        stale,
    };
}
const CHEST_PROP = 'chest';
/**
 * What would be wrong with the edited world, in words for the editor. The
 * same checks as `buildCollisionGrid` (a zone that fails them does not load),
 * plus: an edited prop stands on open ground, clear of any other blocking
 * thing, and every tile a player could reach in the generated world is still
 * reachable, unless something now stands on it.
 */
export function worldEditProblems(base, edited) {
    const problems = edited.stale.map((what) => `Introuvable dans le monde généré : ${what}`);
    const layout = edited.layout;
    const height = layout.rows.length;
    const width = layout.rows[0]?.length ?? 0;
    const inside = (box) => box.x0 >= 0 && box.y0 >= 0 && box.x1 < width && box.y1 < height;
    const open = (x, y) => layout.rows[y]?.[x] === '.';
    // How many blocking things cover each tile: an edited prop on a tile
    // counted twice overlaps something.
    const covered = new Uint8Array(width * height);
    const blockers = [];
    for (const { placement } of edited.props) {
        if (PROPS[placement.prop]?.blocks === true) {
            blockers.push({
                name: placement.prop,
                box: footprintOf(placement),
                of: placement,
                door: placement,
            });
        }
    }
    for (const chest of layout.chests) {
        blockers.push({
            name: `coffre ${chest.id}`,
            box: footprintOf({ ...chest, prop: CHEST_PROP }),
            of: chest,
        });
    }
    for (const { box, door } of blockers) {
        for (let y = Math.max(0, box.y0); y <= Math.min(height - 1, box.y1); y += 1) {
            for (let x = Math.max(0, box.x0); x <= Math.min(width - 1, box.x1); x += 1) {
                // A dungeon's door is walked through (ADR 0027).
                if (door !== undefined && inDoorway(door, x, y))
                    continue;
                covered[y * width + x] = Math.min(255, (covered[y * width + x] ?? 0) + 1);
            }
        }
    }
    const generated = new Set(base.props.map((prop) => JSON.stringify(prop)));
    const baseChests = new Set(base.chests.map((chest) => JSON.stringify(chest)));
    const at = (p) => `(${String(p.x)}, ${String(p.y)})`;
    const overlapped = (self, x, y) => blockers.find((other) => other.of !== self &&
        x >= other.box.x0 &&
        x <= other.box.x1 &&
        y >= other.box.y0 &&
        y <= other.box.y1)?.name ?? 'un autre décor';
    for (const { placement } of edited.props) {
        if (generated.has(JSON.stringify(placement)))
            continue;
        const box = footprintOf(placement);
        const name = `${placement.prop} ${at(placement)}`;
        if (!inside(box)) {
            problems.push(`${name} déborde de la carte`);
            continue;
        }
        const blocks = PROPS[placement.prop]?.blocks === true;
        const problem = firstTile(box, (x, y) => {
            if (!open(x, y))
                return `${name} pose sur un sol infranchissable en ${at({ x, y })}`;
            if (blocks && (covered[y * width + x] ?? 0) > 1) {
                return `${name} chevauche ${overlapped(placement, x, y)} en ${at({ x, y })}`;
            }
            return undefined;
        });
        if (problem !== undefined)
            problems.push(problem);
    }
    for (const chest of layout.chests) {
        if (baseChests.has(JSON.stringify(chest)))
            continue;
        const box = footprintOf({ ...chest, prop: CHEST_PROP });
        if (!inside(box)) {
            problems.push(`Le coffre ${chest.id} déborde de la carte`);
            continue;
        }
        const problem = firstTile(box, (x, y) => {
            if (!open(x, y))
                return `Le coffre ${chest.id} pose sur un sol infranchissable`;
            if ((covered[y * width + x] ?? 0) > 1) {
                return `Le coffre ${chest.id} chevauche ${overlapped(chest, x, y)} en ${at({ x, y })}`;
            }
            return undefined;
        });
        if (problem !== undefined)
            problems.push(problem);
    }
    const walkable = new Uint8Array(width * height);
    for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
            walkable[y * width + x] = open(x, y) && covered[y * width + x] === 0 ? 1 : 0;
        }
    }
    const walk = (p) => p.x >= 0 && p.y >= 0 && p.x < width && p.y < height && walkable[p.y * width + p.x] === 1;
    if (!walk(layout.spawn))
        problems.push(`L'arrivée des joueurs ${at(layout.spawn)} est bouchée`);
    if (!walk(layout.respawn)) {
        problems.push(`Le point de réapparition ${at(layout.respawn)} est bouché`);
    }
    for (const portal of layout.portals) {
        if (!walk(portal.arrival)) {
            problems.push(`L'arrivée du portail de ${portal.name} ${at(portal.arrival)} est bouchée`);
        }
    }
    for (const den of layout.dens) {
        if (!walk(den))
            problems.push(`${den.mob} ${at(den)} est dans un mur`);
    }
    for (const exit of layout.exits) {
        for (let y = exit.y; y < exit.y + exit.height; y += 1) {
            for (let x = exit.x; x < exit.x + exit.width; x += 1) {
                if (!walk({ x, y }))
                    problems.push(`L'entrée de grotte ${at({ x, y })} est bouchée`);
            }
        }
    }
    // Reachability: from the arrival, and from the summits' ladders (a summit
    // is joined by its cave, not on foot).
    const seeds = [
        base.spawn,
        ...base.caves.flatMap((cave) => [
            { x: cave.mouth.x, y: cave.mouth.y + 2 },
            { x: cave.ladder.x, y: cave.ladder.y + 2 },
        ]),
    ];
    const before = reach(width, height, baseWalkable(base, width, height), seeds);
    const after = reach(width, height, walkable, seeds);
    let lost = 0;
    let example;
    for (let i = 0; i < width * height; i += 1) {
        if (before[i] === 1 && walkable[i] === 1 && after[i] === 0) {
            lost += 1;
            example ??= { x: i % width, y: Math.floor(i / width) };
        }
    }
    if (example !== undefined) {
        problems.push(`${String(lost)} cases ne sont plus accessibles, par exemple ${at(example)}`);
    }
    for (const den of layout.dens) {
        if (walk(den) && after[den.y * width + den.x] === 0) {
            problems.push(`${den.mob} ${at(den)} est hors d'atteinte`);
        }
    }
    for (const chest of layout.chests) {
        const front = [0, 1].some((dx) => after[(chest.y + 1) * width + chest.x + dx] === 1);
        if (!front)
            problems.push(`Le coffre ${chest.id} ${at(chest)} est hors d'atteinte`);
    }
    return problems;
}
/** The first message `test` gives for a tile of the box, row by row. */
function firstTile(box, test) {
    for (let y = box.y0; y <= box.y1; y += 1) {
        for (let x = box.x0; x <= box.x1; x += 1) {
            const message = test(x, y);
            if (message !== undefined)
                return message;
        }
    }
    return undefined;
}
function baseWalkable(base, width, height) {
    const walkable = new Uint8Array(width * height);
    for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
            walkable[y * width + x] = base.rows[y]?.[x] === '.' ? 1 : 0;
        }
    }
    const block = (box) => {
        for (let y = Math.max(0, box.y0); y <= Math.min(height - 1, box.y1); y += 1) {
            for (let x = Math.max(0, box.x0); x <= Math.min(width - 1, box.x1); x += 1) {
                walkable[y * width + x] = 0;
            }
        }
    };
    for (const placed of base.props) {
        if (PROPS[placed.prop]?.blocks !== true)
            continue;
        block(footprintOf(placed));
        const box = footprintOf(placed);
        for (let x = box.x0; x <= box.x1; x += 1) {
            if (inDoorway(placed, x, placed.y) && base.rows[placed.y]?.[x] === '.') {
                walkable[placed.y * width + x] = 1;
            }
        }
    }
    for (const chest of base.chests)
        block(footprintOf({ ...chest, prop: CHEST_PROP }));
    return walkable;
}
function reach(width, height, walkable, seeds) {
    const seen = new Uint8Array(width * height);
    const queue = [];
    for (const seed of seeds) {
        const i = seed.y * width + seed.x;
        if (walkable[i] === 1 && seen[i] === 0) {
            seen[i] = 1;
            queue.push(i);
        }
    }
    // The queue grows while it is walked: an array iterator sees what is pushed.
    for (const i of queue) {
        const x = i % width;
        for (const next of [i - width, i + width, x > 0 ? i - 1 : -1, x < width - 1 ? i + 1 : -1]) {
            if (next < 0 || next >= width * height || seen[next] === 1 || walkable[next] === 0)
                continue;
            seen[next] = 1;
            queue.push(next);
        }
    }
    return seen;
}
//# sourceMappingURL=apply-world-edits.js.map